import { useState, useEffect } from 'react';
import { createResource,getResources,getResourceById, updateResource} from '../../../router/api';
import toast from 'react-hot-toast';

interface Employe {
  id: number;
  matricule: string;
  prenom: string;
  nom: string;
  poste: string;
  departement?: string;
}

interface BulletinPaie {
  id: number;
  employe_id: number;
  annee: number;
  mois: number;
  salaire_base: number;
  primes: number;
  indemnites: number;
  heures_sup_25: number;
  heures_sup_50: number;
  salaire_brut: number;
  cnps_salarie: number;
  irpp: number;
  autres_deductions: number;
  total_deductions: number;
  salaire_net: number;
  net_a_payer: number;
  cnps_patron: number;
  accidents_travail: number;
  formation_professionnelle: number;
  total_charges_patronales: number;
  statut_paiement: 'EN_ATTENTE' | 'PAYE' | 'PAYE_PARTIEL';
  observations?: string;
  employe: Employe;
}

interface PayrollListProps {
  onCreatePayroll: (employe: Employe) => void;
}

export default function PayrollList({ onCreatePayroll }: PayrollListProps) {
  const [payrolls, setPayrolls] = useState<BulletinPaie[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [monthFilter, setMonthFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedPayroll, setSelectedPayroll] = useState<BulletinPaie | null>(null);

  // Chargement des bulletins avec employé
  useEffect(() => {
    const loadPayrolls = async () => {
      setLoading(true);
      try {
        const response = await getResources(`bulletins-paie`);
        // Assure-toi que ton endpoint charge la relation employe : ->with('employe')
        setPayrolls(response.data);
      } catch (error: any) {
        toast.error('Erreur lors du chargement des fiches de paie');
        console.error(error);
      } finally {
        setLoading(false);
      }
    };

    loadPayrolls();
  }, []);

  // Filtrage
  const filteredPayrolls = payrolls.filter(payroll => {
    const employeeName = `${payroll.employe.prenom} ${payroll.employe.nom}`.toLowerCase();
    const payrollNumber = `PAIE-${payroll.annee}-${String(payroll.mois).padStart(2, '0')}-${payroll.employe.matricule}`;

    const matchesSearch = employeeName.includes(searchTerm.toLowerCase()) ||
                          payrollNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          payroll.employe.poste.toLowerCase().includes(searchTerm.toLowerCase());

    const periodKey = `${payroll.annee}-${String(payroll.mois).padStart(2, '0')}`;
    const matchesMonth = !monthFilter || periodKey === monthFilter;

    const matchesStatus = statusFilter === 'all' ||
                          (statusFilter === 'PAYE' && payroll.statut_paiement === 'PAYE') ||
                          (statusFilter === 'EN_ATTENTE' && payroll.statut_paiement === 'EN_ATTENTE') ||
                          (statusFilter === 'PAYE_PARTIEL' && payroll.statut_paiement === 'PAYE_PARTIEL');

    return matchesSearch && matchesMonth && matchesStatus;
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'PAYE': return 'bg-green-100 text-green-800';
      case 'EN_ATTENTE': return 'bg-yellow-100 text-yellow-800';
      case 'PAYE_PARTIEL': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Statistiques
// ================= STATISTIQUES CORRIGÉES =================

// Total fiches
const totalFiches = payrolls.length;

// Conversion sécurisée en nombre
const toNumber = (value: any) => Number(value) || 0;

// Statistiques globales (toutes fiches)
const totalGrossSalary = payrolls.reduce(
  (sum, p) => sum + toNumber(p.salaire_brut),
  0
);

const totalNetSalary = payrolls.reduce(
  (sum, p) => sum + toNumber(p.net_a_payer),
  0
);

const totalChargesPatronales = payrolls.reduce(
  (sum, p) => sum + toNumber(p.total_charges_patronales),
  0
);

  // Générer les 12 derniers mois
  const months = Array.from({ length: 12 }, (_, i) => {
    const date = new Date();
    date.setMonth(date.getMonth() - i);
    return {
      key: date.toISOString().slice(0, 7),
      label: date.toLocaleDateString('fr-FR', { year: 'numeric', month: 'long' })
    };
  });

  const handlePrintBulletin = (bulletin: BulletinPaie) => {
  const printWindow = window.open('', '_blank');
  if (!printWindow) {
    toast.error("Impossible d'ouvrir la fenêtre d'impression. Vérifiez vos paramètres de bloqueur de pop-up.");
    return;
  }

  // Calculs utiles
  const salaireBrut = bulletin.salaire_brut;
  const deductions = bulletin.total_deductions;
  const netAPayer = bulletin.net_a_payer;
  const chargesPatronales = bulletin.total_charges_patronales;
  const coutTotalEmployeur = salaireBrut + chargesPatronales;

  // Formatage de la période
  const periode = new Date(bulletin.annee, bulletin.mois - 1).toLocaleDateString('fr-FR', {
    year: 'numeric',
    month: 'long'
  });

  // Numéro du bulletin
  const numeroBulletin = `PAIE-${bulletin.annee}-${String(bulletin.mois).padStart(2, '0')}-${bulletin.employe.matricule}`;

  // URL du logo — Remplace par ton vrai logo !
  const logoUrl = "https://via.placeholder.com/300x120/1e40af/ffffff?text=GROUPE+MAI+MAB+SERVICES";

  const html = `<!DOCTYPE html>
<html lang="fr">
<head>
  <meta charset="UTF-8">
  <title>Bulletin de Paie - ${numeroBulletin}</title>
  <style>
    body {
      font-family: 'Segoe UI', Arial, sans-serif;
      margin: 0;
      padding: 0;
      background: #f5f7fa;
      color: #333;
    }
    .container {
      max-width: 900px;
      margin: 40px auto;
      background: white;
      padding: 50px;
      border-radius: 12px;
      box-shadow: 0 15px 35px rgba(0,0,0,0.1);
    }
    .header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding-bottom: 30px;
      border-bottom: 5px solid #1e40af;
      margin-bottom: 40px;
    }
    .logo {
      max-height: 100px;
      max-width: 320px;
      object-fit: contain;
    }
    .title {
      text-align: right;
    }
    .title h1 {
      font-size: 36px;
      color: #1e40af;
      margin: 0;
      font-weight: bold;
    }
    .title h2 {
      font-size: 24px;
      color: #1e3a8a;
      margin: 10px 0 0;
    }

    .info-grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 50px;
      margin: 40px 0;
    }
    .info-block h3 {
      color: #1e40af;
      font-size: 20px;
      border-bottom: 3px solid #dbeafe;
      padding-bottom: 10px;
      margin-bottom: 20px;
    }
    .info-block p {
      margin: 12px 0;
      line-height: 1.7;
      font-size: 15px;
    }
    .info-block strong {
      color: #1e3a8a;
    }

    .remuneration-table {
      width: 100%;
      border-collapse: collapse;
      margin: 40px 0;
      font-size: 16px;
    }
    .remuneration-table th,
    .remuneration-table td {
      border: 1px solid #e2e8f0;
      padding: 16px;
      text-align: left;
    }
    .remuneration-table th {
      background: #eff6ff;
      color: #1e40af;
      font-weight: 600;
    }
    .text-right { text-align: right; }
    .positive { color: #16a34a; font-weight: 600; }
    .negative { color: #dc2626; font-weight: 600; }

    .totals {
      float: right;
      width: 480px;
      margin-top: 40px;
      font-size: 18px;
    }
    .total-line {
      display: flex;
      justify-content: space-between;
      padding: 14px 0;
      border-top: 1px solid #e2e8f0;
    }
    .total-final {
      font-size: 24px;
      font-weight: bold;
      color: #1e40af;
      border-top: 4px double #2563eb;
      padding-top: 16px;
      background: #f0f9ff;
    }

    .employer-cost {
      margin-top: 60px;
      padding: 25px;
      background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
      border-radius: 12px;
      border-left: 6px solid #2563eb;
      clear: both;
    }

    .observations {
      margin-top: 60px;
      padding: 20px;
      background: #fefce8;
      border-radius: 10px;
      border-left: 5px solid #f59e0b;
    }

    .signature-section {
      margin-top: 120px;
      page-break-inside: avoid;
      clear: both;
    }
    .signature-grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 120px;
      margin-top: 50px;
    }
    .signature-block {
      text-align: center;
    }
    .signature-label {
      font-weight: bold;
      font-size: 18px;
      margin-bottom: 30px;
      color: #1e40af;
    }
    .signature-line {
      border-top: 3px dotted #444;
      height: 150px;
      margin: 30px 0;
      position: relative;
    }
    .signature-line::after {
      content: "Signature + Cachet";
      position: absolute;
      bottom: -40px;
      left: 50%;
      transform: translateX(-50%);
      font-size: 15px;
      color: #555;
      background: white;
      padding: 0 30px;
      font-weight: bold;
    }

    .footer {
      margin-top: 100px;
      text-align: center;
      color: #6b7280;
      font-size: 13px;
      padding-top: 20px;
      border-top: 1px solid #e5e7eb;
    }

    @media print {
      body { background: white; margin: 0; }
      .container { box-shadow: none; padding: 30px; border-radius: 0; }
      .no-print { display: none; }
    }
  </style>
</head>
<body>
  <div class="container">

    <!-- En-tête avec logo -->
    <div class="header">
      <img src="${logoUrl}" alt="Logo entreprise" class="logo">
      <div class="title">
        <h1>BULLETIN DE PAIE</h1>
        <h2>N° ${numeroBulletin}</h2>
      </div>
    </div>

    <!-- Informations employé et période -->
    <div class="info-grid">
      <div class="info-block">
        <h3>Employé</h3>
        <p><strong>Nom :</strong> ${bulletin.employe.nom.toUpperCase()} ${bulletin.employe.prenom}</p>
        <p><strong>Matricule :</strong> ${bulletin.employe.matricule}</p>
        <p><strong>Poste :</strong> ${bulletin.employe.poste}</p>
        <p><strong>Département :</strong> ${bulletin.employe.departement || 'Non défini'}</p>
      </div>

      <div class="info-block">
        <h3>Période de paie</h3>
        <p><strong>Mois :</strong> ${periode}</p>
        <p><strong>Statut :</strong> 
          <span style="padding: 6px 12px; border-radius: 20px; font-weight: bold; font-size: 14px; 
            background: ${bulletin.statut_paiement === 'PAYE' ? '#dcfce7' : bulletin.statut_paiement === 'PAYE_PARTIEL' ? '#fef3c7' : '#fef9c3'};
            color: ${bulletin.statut_paiement === 'PAYE' ? '#166534' : bulletin.statut_paiement === 'PAYE_PARTIEL' ? '#92400e' : '#92400e'};">
            ${bulletin.statut_paiement === 'PAYE' ? 'Payée' : bulletin.statut_paiement === 'PAYE_PARTIEL' ? 'Payée Partiellement' : 'En Attente'}
          </span>
        </p>
        ${bulletin.date_paiement ? `<p><strong>Date de paiement :</strong> ${new Date(bulletin.date_paiement).toLocaleDateString('fr-FR')}</p>` : ''}
      </div>
    </div>

    <!-- Tableau rémunération -->
    <table class="remuneration-table">
      <tbody>
        <tr>
          <td>Salaire de base</td>
          <td class="text-right">${bulletin.salaire_base.toLocaleString()} GNF</td>
        </tr>
        ${bulletin.primes > 0 ? `
        <tr>
          <td class="positive">Primes</td>
          <td class="text-right positive">+ ${bulletin.primes.toLocaleString()} GNF</td>
        </tr>` : ''}
        ${bulletin.indemnites > 0 ? `
        <tr>
          <td class="positive">Indemnités</td>
          <td class="text-right positive">+ ${bulletin.indemnites.toLocaleString()} GNF</td>
        </tr>` : ''}
        <tr class="border-t-2 border-gray-800">
          <td class="font-bold text-lg">Salaire Brut</td>
          <td class="text-right font-bold text-lg">${salaireBrut.toLocaleString()} GNF</td>
        </tr>
        <tr>
          <td class="negative">Cotisations salariales (CNPS, IRPP, etc.)</td>
          <td class="text-right negative">− ${deductions.toLocaleString()} GNF</td>
        </tr>
        <tr class="border-t-4 border-gray-900 bg-green-50">
          <td class="font-bold text-xl">Net à Payer</td>
          <td class="text-right font-bold text-xl text-green-700">${netAPayer.toLocaleString()} GNF</td>
        </tr>
      </tbody>
    </table>

    <!-- Coût employeur -->
    <div class="employer-cost">
      <div class="flex justify-between items-center text-xl font-bold">
        <span>Coût Total pour l'Employeur</span>
        <span class="text-blue-700">${coutTotalEmployeur.toLocaleString()} GNF</span>
      </div>
      <p class="text-sm text-gray-600 mt-3 text-center">
        Inclut les charges patronales : CNPS, accidents du travail, formation professionnelle...
      </p>
    </div>

    <!-- Observations -->
    ${bulletin.observations ? `
    <div class="observations">
      <strong>Observations :</strong><br><br>
      ${bulletin.observations.replace(/\n/g, '<br>')}
    </div>` : ''}

    <!-- Signature -->
    <div class="signature-section">
      <h3 style="text-align: center; color: #1e40af; font-size: 24px; margin-bottom: 60px;">
        ACCUSÉ DE RÉCEPTION DU BULLETIN DE PAIE
      </h3>

      <div class="signature-grid">
        <div class="signature-block">
          <div class="signature-label">
            Employeur<br>
            <small>(Cachet + Signature)</small>
          </div>
          <div class="signature-line"></div>
        </div>

        <div class="signature-block">
          <div class="signature-label">
            Salarié<br>
            <small>(Signature)</small>
          </div>
          <div class="signature-line"></div>
        </div>
      </div>

      <p style="text-align: center; margin-top: 80px; font-style: italic; color: #555; font-size: 16px;">
        "J'accuse réception du présent bulletin de paie et en prends connaissance."
      </p>
    </div>

    <div class="footer">
      Document généré le ${new Date().toLocaleDateString('fr-FR')} à ${new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })} 
      — GROUPE MAI MAB SERVICES
    </div>
  </div>
</body>
</html>`;

  printWindow.document.write(html);
  printWindow.document.close();
  printWindow.focus();

  // Petite pause pour laisser le temps au navigateur de charger le CSS et le logo
  setTimeout(() => {
    printWindow.print();
    // Optionnel : fermer la fenêtre après impression
    // printWindow.close();
  }, 1000);
};
  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-gray-500">Chargement des fiches de paie...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Statistiques */}
      {/* Statistiques — Calculées sur toutes les fiches de paie */}
    <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
      <div className="bg-white p-6 rounded-lg shadow-sm border">
        <div className="flex items-center">
          <div className="p-3 rounded-full bg-blue-100 mr-4">
            <i className="ri-file-list-3-line text-xl text-blue-600"></i>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-600">Total Fiches de Paie</p>
            <p className="text-2xl font-bold text-gray-900">{payrolls.length}</p>
          </div>
        </div>
      </div>

      <div className="bg-white p-6 rounded-lg shadow-sm border">
        <div className="flex items-center">
          <div className="p-3 rounded-full bg-green-100 mr-4">
            <i className="ri-money-euro-circle-line text-xl text-green-600"></i>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-600">Salaire Brut Total</p>
            <p className="text-2xl font-bold text-gray-900">{totalGrossSalary.toLocaleString()} GNF</p>
          </div>
        </div>
      </div>

      <div className="bg-white p-6 rounded-lg shadow-sm border">
        <div className="flex items-center">
          <div className="p-3 rounded-full bg-purple-100 mr-4">
            <i className="ri-wallet-3-line text-xl text-purple-600"></i>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-600">Net à Payer Total</p>
            <p className="text-2xl font-bold text-gray-900">{totalNetSalary.toLocaleString()} GNF</p>
          </div>
        </div>
      </div>

      <div className="bg-white p-6 rounded-lg shadow-sm border">
        <div className="flex items-center">
          <div className="p-3 rounded-full bg-orange-100 mr-4">
            <i className="ri-calculator-line text-xl text-orange-600"></i>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-600">Charges Patronales Totales</p>
            <p className="text-2xl font-bold text-gray-900">{totalChargesPatronales.toLocaleString()} GNF</p>
            {/* ↑ Ici c'était totalCharges avant → corrigé en totalChargesPatronales */}
          </div>
        </div>
      </div>
    </div>

      {/* Filtres */}
      <div className="bg-white p-6 rounded-lg shadow-sm border">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
          <div className="flex flex-col md:flex-row space-y-4 md:space-y-0 md:space-x-4">
            <div className="relative">
              <i className="ri-search-line absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
              <input
                type="text"
                placeholder="Rechercher une fiche de paie..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm w-full md:w-80"
              />
            </div>

            <select
              value={monthFilter}
              onChange={(e) => setMonthFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
            >
              <option value="">Tous les mois</option>
              {months.map(m => (
                <option key={m.key} value={m.key}>{m.label}</option>
              ))}
            </select>

            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
            >
              <option value="all">Tous les statuts</option>
              <option value="EN_ATTENTE">En Attente</option>
              <option value="PAYE">Payée</option>
              <option value="PAYE_PARTIEL">Payée Partiellement</option>
            </select>
          </div>

          <div className="text-sm text-gray-600">
            {filteredPayrolls.length} fiche(s) trouvée(s)
          </div>
        </div>
      </div>

      {/* Liste */}
      <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">N° Fiche</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Employé</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Période</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Salaire Brut</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Charges Patronales</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Net à Payer</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Statut</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredPayrolls.map((payroll) => {
                const payrollNumber = `PAIE-${payroll.annee}-${String(payroll.mois).padStart(2, '0')}-${payroll.employe.matricule}`;
                return (
                  <tr key={payroll.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {payrollNumber}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-8 w-8">
                          <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center">
                            <span className="text-xs font-medium text-blue-600">
                              {payroll.employe.prenom.charAt(0)}{payroll.employe.nom.charAt(0)}
                            </span>
                          </div>
                        </div>
                        <div className="ml-3">
                          <div className="text-sm font-medium text-gray-900">
                            {payroll.employe.prenom} {payroll.employe.nom}
                          </div>
                          <div className="text-sm text-gray-500">{payroll.employe.poste}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {new Date(payroll.annee, payroll.mois - 1).toLocaleDateString('fr-FR', { year: 'numeric', month: 'long' })}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {payroll.salaire_brut.toLocaleString()} GNF
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {payroll.total_charges_patronales.toLocaleString()} GNF
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {payroll.net_a_payer.toLocaleString()} GNF
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(payroll.statut_paiement)}`}>
                        {payroll.statut_paiement === 'PAYE' ? 'Payée' :
                         payroll.statut_paiement === 'EN_ATTENTE' ? 'En Attente' :
                         'Payée Partiellement'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => setSelectedPayroll(payroll)}
                          className="text-blue-600 hover:text-blue-900 p-1 rounded"
                          title="Voir les détails"
                        >
                          <i className="ri-eye-line text-lg"></i>
                        </button>
                        <button
                         onClick={() => handlePrintBulletin(payroll)} 
                          className="text-green-600 hover:text-green-900 p-1 rounded"
                          title="Télécharger PDF"
                        >
                          <i className="ri-download-line text-lg"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {filteredPayrolls.length === 0 && !loading && (
            <div className="text-center py-12 text-gray-500">
              Aucune fiche de paie trouvée
            </div>
          )}
        </div>
      </div>

      {/* Modal Détails */}
      {selectedPayroll && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-2/3 shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg font-medium text-gray-900">
                Fiche de Paie - PAIE-{selectedPayroll.annee}-{String(selectedPayroll.mois).padStart(2, '0')}-{selectedPayroll.employe.matricule}
              </h3>
              <button onClick={() => setSelectedPayroll(null)} className="text-gray-400 hover:text-gray-600">
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>

            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="text-md font-medium text-gray-900 mb-3">Employé</h4>
                  <div className="space-y-2">
                    <div className="flex justify-between"><span className="text-sm text-gray-600">Nom:</span><span className="text-sm font-medium">{selectedPayroll.employe.prenom} {selectedPayroll.employe.nom}</span></div>
                    <div className="flex justify-between"><span className="text-sm text-gray-600">Matricule:</span><span className="text-sm font-medium">{selectedPayroll.employe.matricule}</span></div>
                    <div className="flex justify-between"><span className="text-sm text-gray-600">Poste:</span><span className="text-sm font-medium">{selectedPayroll.employe.poste}</span></div>
                    <div className="flex justify-between"><span className="text-sm text-gray-600">Département:</span><span className="text-sm font-medium">{selectedPayroll.employe.departement || '-'}</span></div>
                  </div>
                </div>

                <div>
                  <h4 className="text-md font-medium text-gray-900 mb-3">Période</h4>
                  <div className="space-y-2">
                    <div className="flex justify-between"><span className="text-sm text-gray-600">Mois:</span><span className="text-sm font-medium">
                      {new Date(selectedPayroll.annee, selectedPayroll.mois - 1).toLocaleDateString('fr-FR', { year: 'numeric', month: 'long' })}
                    </span></div>
                    <div className="flex justify-between"><span className="text-sm text-gray-600">Statut:</span>
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(selectedPayroll.statut_paiement)}`}>
                        {selectedPayroll.statut_paiement === 'PAYE' ? 'Payée' : selectedPayroll.statut_paiement === 'EN_ATTENTE' ? 'En Attente' : 'Payée Partiellement'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <h4 className="text-md font-medium text-gray-900 mb-3">Rémunération</h4>
                <div className="bg-gray-50 p-4 rounded-lg space-y-3">
                  <div className="flex justify-between"><span className="text-sm text-gray-600">Salaire de base:</span><span>{selectedPayroll.salaire_base.toLocaleString()} GNF</span></div>
                  {selectedPayroll.primes > 0 && <div className="flex justify-between text-green-600"><span>Primes:</span><span>+{selectedPayroll.primes.toLocaleString()} GNF</span></div>}
                  {selectedPayroll.indemnites > 0 && <div className="flex justify-between text-green-600"><span>Indemnités:</span><span>+{selectedPayroll.indemnites.toLocaleString()} GNF</span></div>}
                  {(selectedPayroll.heures_sup_25 + selectedPayroll.heures_sup_50) > 0 && 
                    <div className="flex justify-between text-green-600"><span>Heures supplémentaires:</span><span>+{(selectedPayroll.heures_sup_25 * 1.25 + selectedPayroll.heures_sup_50 * 1.5) * (selectedPayroll.salaire_base / 173.33)}.toFixed(0) GNF</span></div>
                  }
                  <div className="border-t pt-2 font-medium"><div className="flex justify-between"><span>Salaire Brut:</span><span>{selectedPayroll.salaire_brut.toLocaleString()} GNF</span></div></div>
                  <div className="flex justify-between text-red-600"><span>Déductions salarié:</span><span>-{selectedPayroll.total_deductions.toLocaleString()} GNF</span></div>
                  <div className="border-t pt-2 text-lg font-bold text-green-600"><div className="flex justify-between"><span>Net à Payer:</span><span>{selectedPayroll.net_a_payer.toLocaleString()} GNF</span></div></div>
                </div>
              </div>

              <div>
                <h4 className="text-md font-medium text-gray-900 mb-3">Charges Patronales</h4>
                <div className="bg-blue-50 p-4 rounded-lg">
                  <div className="flex justify-between text-blue-600 font-medium">
                    <span>Total charges:</span>
                    <span>{selectedPayroll.total_charges_patronales.toLocaleString()} GNF</span>
                  </div>
                  <div className="flex justify-between mt-2 pt-2 border-t border-blue-200 font-bold">
                    <span>Coût total employeur:</span>
                    <span>{(selectedPayroll.salaire_brut + selectedPayroll.total_charges_patronales).toLocaleString()} GNF</span>
                  </div>
                </div>
              </div>

              {selectedPayroll.observations && (
                <div>
                  <h4 className="text-md font-medium text-gray-900 mb-3">Observations</h4>
                  <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded-lg">{selectedPayroll.observations}</p>
                </div>
              )}
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button onClick={() => setSelectedPayroll(null)} className="px-4 py-2 text-sm text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md">
                Fermer
              </button>
              <button onClick={() => handlePrintBulletin(selectedPayroll)} className="px-4 py-2 text-sm text-white bg-green-600 hover:bg-green-700 rounded-md flex items-center space-x-2">
                <i className="ri-download-line"></i>
                <span>Télécharger PDF</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}